import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MaskedReveal } from "@/components/humble/AnimatedText";
import { scrollToId } from "@/lib/lenis";
import { HUMBLE } from "@/constants/testIds";
import Painting from "@/components/humble/Painting";

/**
 * Particles — drifting light motes.
 */
function LightParticles({ count = 14 }) {
  const reduce = useReducedMotion();
  if (reduce) return null;
  const items = Array.from({ length: count });
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-[15] overflow-hidden">
      {items.map((_, i) => {
        const left = Math.random() * 100;
        const size = 3 + Math.random() * 6;
        const delay = -Math.random() * 18;
        const dur = 14 + Math.random() * 10;
        return (
          <span
            key={i}
            className="absolute rounded-full bg-gold"
            style={{
              left: `${left}%`,
              bottom: "-8vh",
              width: size,
              height: size,
              filter: "blur(2px)",
              opacity: 0,
              animation: `drift ${dur}s linear ${delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative isolate min-h-[100svh] w-full overflow-hidden bg-earth text-cream"
      data-testid="section-hero"
    >
      {/* Background painting w/ Ken-Burns + parallax */}
      <motion.div
        style={{ y: reduce ? "0%" : y }}
        className="absolute inset-0 z-0"
      >
        <div className="absolute inset-0 animate-ken-burns will-change-transform">
          <img
            src="/art/hero-pasture-sunrise.png"
            alt="A painted Ozark pasture at sunrise, warm golden light across tall grass and a weathered fence."
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const p = e.currentTarget.parentElement;
              if (p) p.classList.add("bg-hero-fallback");
            }}
          />
          {/* painterly fallback if image missing */}
          <div
            aria-hidden
            className="absolute inset-0 -z-10"
            style={{
              background:
                "radial-gradient(ellipse at 30% 40%, hsl(38 60% 62%) 0%, hsl(25 45% 30%) 55%, hsl(30 30% 12%) 100%)",
            }}
          />
        </div>
      </motion.div>

      {/* Warm color wash + vignette + grain */}
      <div
        aria-hidden
        className="absolute inset-0 z-[10]"
        style={{
          background:
            "linear-gradient(180deg, hsl(30 40% 20% / 0.15) 0%, hsl(30 40% 12% / 0.35) 45%, hsl(30 40% 8% / 0.85) 100%)",
        }}
      />
      <div className="pointer-events-none absolute inset-0 z-[12] vignette" />
      <div className="pointer-events-none absolute inset-0 z-[13] grain" />
      <LightParticles />

      {/* Top brand */}
      <header className="relative z-[25] mx-auto flex w-full max-w-[1400px] items-center justify-between px-6 pt-8 md:px-12 md:pt-10">
        <a
          href="#hero"
          data-testid={HUMBLE.navHome}
          className="group inline-flex items-baseline gap-3 text-cream/90 hover:text-cream"
        >
          <span
            className="font-display italic text-2xl md:text-[26px] tracking-tight"
            style={{ textShadow: "0 2px 20px hsl(var(--earth)/0.6)" }}
          >
            Humble Roots
          </span>
          <span className="hidden md:inline h-[1px] w-10 bg-cream/40 translate-y-[-6px]" />
          <span className="hidden md:inline font-body uppercase text-[11px] tracking-[0.4em] text-cream/60">
            est. 2020 · Arkansas
          </span>
        </a>
        <button
          data-testid={HUMBLE.navWaitlist}
          onClick={() => scrollToId("waitlist")}
          className="rounded-full border border-cream/25 bg-cream/5 px-5 py-2 font-body text-sm tracking-wide text-cream/90 backdrop-blur-md transition hover:bg-cream/15"
        >
          Save me a seat
        </button>
      </header>

      {/* Headline block — bottom-left, editorial */}
      <motion.div
        style={{ opacity: reduce ? 1 : opacity }}
        className="relative z-[25] mx-auto flex min-h-[calc(100svh-6rem)] w-full max-w-[1400px] flex-col justify-end px-6 pb-16 md:px-12 md:pb-24"
      >
        <div className="max-w-4xl">
          <motion.div
            initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mb-6 flex items-center gap-3 font-body uppercase text-[11px] tracking-[0.42em] text-cream/70"
          >
            <span className="inline-block h-px w-8 bg-gold/70" />
            A homestead-management app for families with day jobs
          </motion.div>

          <MaskedReveal
            testId={HUMBLE.heroHeadline}
            as="h1"
            className="font-display text-[42px] leading-[1.02] tracking-tight text-cream sm:text-[58px] md:text-[76px] lg:text-[88px]"
            lines={[
              "A calm, simple way",
              <>
                to keep the{" "}
                <em className="not-italic text-gold">homestead</em>
              </>,
              "you love.",
            ]}
            delay={0.25}
            stagger={0.18}
            duration={1.1}
          />

          <motion.p
            initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-2xl font-body text-base md:text-lg text-cream/85"
          >
            Humble Roots holds your animals, their records, health and
            treatments, breeding, and the daily chores — one gentle place for
            families who also work an outside job. Not a spreadsheet. Not a
            farm ERP. Just the tool we wished we&rsquo;d had.
          </motion.p>

          <motion.div
            initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid={HUMBLE.heroCta}
              onClick={() => scrollToId("waitlist")}
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-3.5 font-body text-[15px] font-medium text-earth transition hover:bg-[hsl(var(--gold-deep))] hover:text-cream"
            >
              Save me a seat
              <span className="inline-block transition-transform group-hover:translate-x-1">
                &rarr;
              </span>
            </button>
            <button
              data-testid={HUMBLE.heroSecondary}
              onClick={() => scrollToId("waitlist")}
              className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-6 py-3 font-body text-[15px] text-cream/90 backdrop-blur-md transition hover:bg-cream/10"
            >
              Create an account
            </button>
            <span className="ml-1 font-hand text-xl text-cream/75">
              — we&rsquo;re opening the barn door soon.
            </span>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <div
          data-testid={HUMBLE.scrollHint}
          className="pointer-events-none absolute bottom-4 right-6 md:right-12 flex items-center gap-3 text-cream/60"
        >
          <span className="font-body uppercase text-[10px] tracking-[0.4em]">
            Chapter I &nbsp;·&nbsp; Scroll
          </span>
          <motion.span
            aria-hidden
            animate={reduce ? undefined : { y: [0, 8, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            className="block h-8 w-px bg-cream/50"
          />
        </div>
      </motion.div>
    </section>
  );
}
